/**
 * «Записи» catalogue, part 3 — the web's own screens outside a game (owner 27.09: «поставь все фразы на озвучку»): the
 * puzzle trainer (start, solved, miss, hint, solution, summary), «Путь пешки», the new-game wizard's strong-opponent
 * phrase. The web says each as a clip twin (apps/web/src/coach/clips/shellTwin.ts) whose bubble is exactly one of these
 * wordings — one line per meaning (a line's wordings stand in for each other when one has no take), numbers in words,
 * no names, no squares. Placeholders: see ./catalog.ts.
 *
 * The web's builders (apps/web/src/features/puzzles/puzzleCoach.ts, apps/web/src/app/newGame.ts) keep their own texts;
 * puzzleVoice.test.ts and shellPhrases.test.ts (web) walk every variant and keep each bubble equal to these wordings. A
 * number, a theme or a stage is a line of its own (the wordings of a line must be interchangeable), and a bubble says at
 * most two lines. New wordings are APPENDED to a line, never inserted (a wording's number is its recording's key).
 */
import type { ClipCatalogLine, ClipWording } from '@gambit/shared';

const w = (...texts: string[]): ClipWording[] => texts.map((t) => ({ t }));

/** One whole line per key, with one wording each: `<prefix><key>` says exactly its text. */
const oneEach = (prefix: string, texts: Readonly<Record<string, string>>): ClipCatalogLine[] =>
  Object.entries(texts).map(([key, t]): ClipCatalogLine => ({ id: `${prefix}${key}`, role: 'whole', wordings: w(t) }));

// ───────────────────────── the puzzle trainer (apps/web/src/features/puzzles/puzzleCoach.ts) ─────────────────────────

/** A session starts: the opener, then the theme of a theme session — or, in a mixed one, a calm word. */
const PUZZLE_START: readonly ClipCatalogLine[] = [
  { id: 'screen.puzzle.start', role: 'whole', wordings: w('Решаем задачи!', 'Время задачек!', 'Разомнём голову!') },
  { id: 'screen.puzzle.calm', role: 'whole', wordings: w('Не спеши — времени сколько угодно.', 'Часов тут нет, думай спокойно.', 'Сначала смотрим — потом ходим!') },
];

/**
 * The theme, one line per Lichess puzzle theme (`THEME_TITLES_RU` of @gambit/content, said: numbers in words, a title
 * with quotes of its own keeps them): after the opener of a theme session and after a solved puzzle of a mixed one.
 * Line id: `screen.puzzle.theme.<theme key>`.
 */
const THEME_SENTENCES: Readonly<Record<string, string>> = {
  // tactics motifs
  advancedPawn: 'Тема — «Далеко ушедшая пешка».',
  attackingF2F7: 'Тема — «Атака на слабый пункт у короля».',
  attraction: 'Тема — «Завлечение».',
  capturingDefender: 'Тема — «Уничтожение защитника».',
  clearance: 'Тема — «Освобождение линии».',
  collinearMove: 'Тема — «Ход вдоль линии».',
  defensiveMove: 'Тема — «Защитный ход».',
  deflection: 'Тема — «Отвлечение».',
  discoveredAttack: 'Тема — «Вскрытое нападение».',
  discoveredCheck: 'Тема — «Вскрытый шах».',
  doubleCheck: 'Тема — «Двойной шах».',
  enPassant: 'Тема — «Взятие на проходе».',
  exposedKing: 'Тема — «Открытый король».',
  fork: 'Тема — «Вилка».',
  hangingPiece: 'Тема — «Незащищённая фигура».',
  interference: 'Тема — «Перекрытие».',
  intermezzo: 'Тема — «Промежуточный ход».',
  kingsideAttack: 'Тема — «Атака на королевском фланге».',
  pin: 'Тема — «Связка».',
  promotion: 'Тема — «Превращение пешки».',
  queensideAttack: 'Тема — «Атака на ферзевом фланге».',
  quietMove: 'Тема — «Тихий ход».',
  sacrifice: 'Тема — «Жертва».',
  skewer: 'Тема — «Сквозной удар».',
  trappedPiece: 'Тема — «Ловля фигуры».',
  underPromotion: 'Тема — «Слабое превращение».',
  xRayAttack: 'Тема — «Рентген».',
  zugzwang: 'Тема — «Цугцванг».',
  castling: 'Тема — «Рокировка».',
  // mates
  mate: 'Тема — «Мат».',
  mateIn1: 'Тема — «Мат одним ходом».',
  mateIn2: 'Тема — «Мат в два хода».',
  mateIn3: 'Тема — «Мат в три хода».',
  mateIn4: 'Тема — «Мат в четыре хода».',
  mateIn5: 'Тема — «Мат в пять ходов и длиннее».',
  anastasiaMate: 'Тема — «Мат Анастасии».',
  arabianMate: 'Тема — «Арабский мат».',
  backRankMate: 'Тема — «Мат на последней линии».',
  balestraMate: 'Тема — «Мат Балестра».',
  blindSwineMate: 'Тема — «Мат двумя ладьями на седьмой».',
  bodenMate: 'Тема — «Мат Бодена».',
  cornerMate: 'Тема — «Мат в углу».',
  doubleBishopMate: 'Тема — «Мат двумя слонами».',
  dovetailMate: 'Тема — мат «ласточкин хвост».',
  epauletteMate: 'Тема — «Эполетный мат».',
  hookMate: 'Тема — «Мат-крючок».',
  killBoxMate: 'Тема — мат «тесная коробка».',
  morphysMate: 'Тема — «Мат Морфи».',
  operaMate: 'Тема — «Оперный мат».',
  pillsburysMate: 'Тема — «Мат Пильсбери».',
  smotheredMate: 'Тема — «Спёртый мат».',
  swallowstailMate: 'Тема — мат «хвост ласточки».',
  triangleMate: 'Тема — «Мат-треугольник».',
  vukovicMate: 'Тема — «Мат Вуковича».',
  // game phases and endgame classes
  opening: 'Тема — «Дебют».',
  middlegame: 'Тема — «Середина партии».',
  endgame: 'Тема — «Эндшпиль».',
  pawnEndgame: 'Тема — «Пешечный эндшпиль».',
  knightEndgame: 'Тема — «Коневой эндшпиль».',
  bishopEndgame: 'Тема — «Слоновый эндшпиль».',
  rookEndgame: 'Тема — «Ладейный эндшпиль».',
  queenEndgame: 'Тема — «Ферзевый эндшпиль».',
  queenRookEndgame: 'Тема — «Ферзь и ладья в эндшпиле».',
  // length
  oneMove: 'Тема — «Задача в один ход».',
  short: 'Тема — «Короткая задача».',
  long: 'Тема — «Длинная задача».',
  veryLong: 'Тема — «Очень длинная задача».',
  // goal
  advantage: 'Тема — «Получи перевес».',
  crushing: 'Тема — «Разгром».',
  equality: 'Тема — «Спаси партию».',
  // origin
  master: 'Тема — «Из партий мастеров».',
  masterVsMaster: 'Тема — «Мастер против мастера».',
  superGM: 'Тема — «Из партий супергроссмейстеров».',
};

/**
 * A solved puzzle: the praise — another mate than the stored one, a streak of clean solutions (a session is at most ten
 * puzzles: three to nine, five and ten are the milestones), a clean solution, one won by persistence —, then the theme.
 */
const PUZZLE_SOLVED: readonly ClipCatalogLine[] = [
  { id: 'screen.puzzle.solved.altMate', role: 'whole', wordings: w('Мат! Твой способ тоже отличный.', 'И так тоже мат! Здорово.') },
  { id: 'screen.puzzle.solved.clean', role: 'whole', wordings: w('Точно! С первой попытки.', 'Верно! Хорошо всё проверено.', 'Есть! Отличное решение.', 'Да! Это было внимательно.') },
  {
    id: 'screen.puzzle.solved.persist',
    role: 'whole',
    wordings: w('Получилось! Упорство победило.', 'Вот и решение! Здорово, что задача доведена до конца.', 'Есть! Вторая попытка — тоже попытка.'),
  },
  { id: 'screen.puzzle.solved.streak.3', role: 'whole', wordings: w('Уже три подряд! Вот это внимательность.', 'Три подряд с первой попытки! Так держать.') },
  { id: 'screen.puzzle.solved.streak.4', role: 'whole', wordings: w('Уже четыре подряд! Вот это внимательность.', 'Четыре подряд с первой попытки! Так держать.') },
  { id: 'screen.puzzle.solved.streak.6', role: 'whole', wordings: w('Уже шесть подряд! Вот это внимательность.', 'Шесть подряд с первой попытки! Так держать.') },
  { id: 'screen.puzzle.solved.streak.7', role: 'whole', wordings: w('Уже семь подряд! Вот это внимательность.', 'Семь подряд с первой попытки! Так держать.') },
  { id: 'screen.puzzle.solved.streak.8', role: 'whole', wordings: w('Уже восемь подряд! Вот это внимательность.', 'Восемь подряд с первой попытки! Так держать.') },
  { id: 'screen.puzzle.solved.streak.9', role: 'whole', wordings: w('Уже девять подряд! Вот это внимательность.', 'Девять подряд с первой попытки! Так держать.') },
  { id: 'screen.puzzle.solved.milestone.5', role: 'whole', wordings: w('И-го-го! Пять задач подряд с первой попытки!') },
  { id: 'screen.puzzle.solved.milestone.10', role: 'whole', wordings: w('И-го-го! Десять задач подряд с первой попытки!') },
];

/** A wrong move: the first one; later a tip (checks and captures, a piece without defence) or the way to a hint. */
const PUZZLE_MISS: readonly ClipCatalogLine[] = [
  { id: 'screen.puzzle.miss.first', role: 'whole', wordings: w('Почти! Попробуй ещё.', 'Не совсем. Посмотри ещё разок.', 'Хорошая мысль, но есть ход сильнее. Попробуй ещё!') },
  { id: 'screen.puzzle.miss.checks', role: 'whole', wordings: w('Проверь шахи, взятия и угрозы.') },
  { id: 'screen.puzzle.miss.undefended', role: 'whole', wordings: w('Посмотри, какая фигура соперника стоит без защиты.') },
  { id: 'screen.puzzle.miss.hint', role: 'whole', wordings: w('Не выходит? Нажми на лампочку — подскажу.', 'Трудная задача. Давай ещё попытку — или возьми подсказку.') },
];

/** The lamp: first the piece in the blue frame (the king too), then the move's arrow; «Показать решение». */
const PUZZLE_HELP: readonly ClipCatalogLine[] = [
  {
    id: 'screen.puzzle.hint.piece',
    role: 'whole',
    byPiece: ['p', 'n', 'b', 'r', 'q', 'k'],
    wordings: w('Посмотри на {коня} в синей рамке. Куда {ему} лучше пойти?', 'Ходить нужно фигурой в синей рамке. Найди для неё лучшую клетку!'),
  },
  { id: 'screen.puzzle.hint.move', role: 'whole', wordings: w('Вот этот ход. Сыграй его и подумай, почему он сильный.', 'Смотри на зелёную стрелку. Сыграй ход и найди, в чём его сила.') },
  { id: 'screen.puzzle.solution', role: 'whole', wordings: w('Смотри, как это работает. Такая картинка ещё встретится — и ты её узнаешь!', 'Показываю решение. Запомни идею — она пригодится.') },
];

/**
 * The end of a session: the opener, then how many were solved at the first try — one line per (solved, total),
 * 1 ≤ solved ≤ total ≤ 10 (ten puzzles, three in the warm-up, fewer when the server has fewer) — or, with none, the
 * effort. Line id: `screen.puzzle.summary.<solved>of<total>`.
 */
const SUMMARY_COUNTS: Readonly<Record<string, string>> = {
  '1of1': 'С первой попытки решено: одна из одной.',
  '1of2': 'С первой попытки решено: одна из двух.',
  '2of2': 'С первой попытки решено: две из двух.',
  '1of3': 'С первой попытки решено: одна из трёх.',
  '2of3': 'С первой попытки решено: две из трёх.',
  '3of3': 'С первой попытки решено: три из трёх.',
  '1of4': 'С первой попытки решено: одна из четырёх.',
  '2of4': 'С первой попытки решено: две из четырёх.',
  '3of4': 'С первой попытки решено: три из четырёх.',
  '4of4': 'С первой попытки решено: четыре из четырёх.',
  '1of5': 'С первой попытки решено: одна из пяти.',
  '2of5': 'С первой попытки решено: две из пяти.',
  '3of5': 'С первой попытки решено: три из пяти.',
  '4of5': 'С первой попытки решено: четыре из пяти.',
  '5of5': 'С первой попытки решено: пять из пяти.',
  '1of6': 'С первой попытки решено: одна из шести.',
  '2of6': 'С первой попытки решено: две из шести.',
  '3of6': 'С первой попытки решено: три из шести.',
  '4of6': 'С первой попытки решено: четыре из шести.',
  '5of6': 'С первой попытки решено: пять из шести.',
  '6of6': 'С первой попытки решено: шесть из шести.',
  '1of7': 'С первой попытки решено: одна из семи.',
  '2of7': 'С первой попытки решено: две из семи.',
  '3of7': 'С первой попытки решено: три из семи.',
  '4of7': 'С первой попытки решено: четыре из семи.',
  '5of7': 'С первой попытки решено: пять из семи.',
  '6of7': 'С первой попытки решено: шесть из семи.',
  '7of7': 'С первой попытки решено: семь из семи.',
  '1of8': 'С первой попытки решено: одна из восьми.',
  '2of8': 'С первой попытки решено: две из восьми.',
  '3of8': 'С первой попытки решено: три из восьми.',
  '4of8': 'С первой попытки решено: четыре из восьми.',
  '5of8': 'С первой попытки решено: пять из восьми.',
  '6of8': 'С первой попытки решено: шесть из восьми.',
  '7of8': 'С первой попытки решено: семь из восьми.',
  '8of8': 'С первой попытки решено: восемь из восьми.',
  '1of9': 'С первой попытки решено: одна из девяти.',
  '2of9': 'С первой попытки решено: две из девяти.',
  '3of9': 'С первой попытки решено: три из девяти.',
  '4of9': 'С первой попытки решено: четыре из девяти.',
  '5of9': 'С первой попытки решено: пять из девяти.',
  '6of9': 'С первой попытки решено: шесть из девяти.',
  '7of9': 'С первой попытки решено: семь из девяти.',
  '8of9': 'С первой попытки решено: восемь из девяти.',
  '9of9': 'С первой попытки решено: девять из девяти.',
  '1of10': 'С первой попытки решено: одна из десяти.',
  '2of10': 'С первой попытки решено: две из десяти.',
  '3of10': 'С первой попытки решено: три из десяти.',
  '4of10': 'С первой попытки решено: четыре из десяти.',
  '5of10': 'С первой попытки решено: пять из десяти.',
  '6of10': 'С первой попытки решено: шесть из десяти.',
  '7of10': 'С первой попытки решено: семь из десяти.',
  '8of10': 'С первой попытки решено: восемь из десяти.',
  '9of10': 'С первой попытки решено: девять из десяти.',
  '10of10': 'С первой попытки решено: десять из десяти.',
};

const PUZZLE_SUMMARY: readonly ClipCatalogLine[] = [
  { id: 'screen.puzzle.summary.opener', role: 'whole', wordings: w('Задачи позади!', 'Готово! Отличная тренировка.', 'Вот и всё на этот раз!') },
  { id: 'screen.puzzle.summary.hard', role: 'whole', wordings: w('Задачи были трудные, и все доведены до конца — это главное.') },
  ...oneEach('screen.puzzle.summary.', SUMMARY_COUNTS),
];

// ───────────────────────── «Путь пешки» (apps/web/src/features/curriculum/CurriculumScreen.tsx) ─────────────────────────

/** The path opens: the child's stage (one line per stage of ten), then where the path leads. */
const PAWN_PATH: readonly ClipCatalogLine[] = [
  ...oneEach('screen.path.stage.', {
    1: 'Это твой путь пешки! Сейчас ты на первой ступени.',
    2: 'Это твой путь пешки! Сейчас ты на второй ступени.',
    3: 'Это твой путь пешки! Сейчас ты на третьей ступени.',
    4: 'Это твой путь пешки! Сейчас ты на четвёртой ступени.',
    5: 'Это твой путь пешки! Сейчас ты на пятой ступени.',
    6: 'Это твой путь пешки! Сейчас ты на шестой ступени.',
    7: 'Это твой путь пешки! Сейчас ты на седьмой ступени.',
    8: 'Это твой путь пешки! Сейчас ты на восьмой ступени.',
    9: 'Это твой путь пешки! Сейчас ты на девятой ступени.',
    10: 'Это твой путь пешки! Сейчас ты на десятой ступени.',
  }),
  { id: 'screen.path.goal', role: 'whole', wordings: w('Шаг за шагом пешка дойдёт до конца и станет ферзём.') },
];

// ───────────────────────── the new-game wizard (apps/web/src/app/newGame.ts) ─────────────────────────

/** A bot far above the child's stage: nameless — a bot's name may be a child's nickname, and names are never recorded. */
const WIZARD: readonly ClipCatalogLine[] = [
  { id: 'screen.wizard.stretch', role: 'whole', wordings: w('Этот соперник играет очень сильно. Попробуем продержаться двадцать ходов?') },
];

export const SCREENS_CATALOG: readonly ClipCatalogLine[] = [
  ...PUZZLE_START,
  ...oneEach('screen.puzzle.theme.', THEME_SENTENCES),
  ...PUZZLE_SOLVED,
  ...PUZZLE_MISS,
  ...PUZZLE_HELP,
  ...PUZZLE_SUMMARY,
  ...PAWN_PATH,
  ...WIZARD,
];
